import * as Sentry from '@sentry/svelte'
import { mutate, query } from '@urql/svelte'
import { ftl, locale, parse_localized } from '/@/multilang'
import { parseISO as _parseISO, addDays, addMilliseconds } from 'date-fns'
import { debounce as _debounce } from 'lodash-es'
import { derived } from 'svelte/store'

import { router } from './router'
import { settings, user } from './store.js'

ftl`
    attachment-error-upload = File upload failed
    attachment-error-upload-rejected = Rejected by server
    attachment-error-upload-read = Failed to read file
    attachment-error-upload-size = File must be smaller than {$param}
    attachment-error-upload-type = File must be one of {$param}
    attachment-error-upload-risk = File looks dangerous
    timespan-validity-later = Later
    timespan-validity-expired = expired
    timespan-validity-starts = From {$start}
    timespan-validity-ends = Until {$end}
    timespan-validity-spans = {$start} until {$end}
    `

export const parseISO = (dd) =>
  dd ? (dd instanceof Date ? dd : _parseISO(dd)) : undefined

export function getDocumentList(schema, callback) {
  schema = `list${schema}Entry`
  return query({
    query: `query {${schema}{nodes{uuid, title, timestamp, validity_start, validity_end, validity_user, categories{title}}}}`,
    requestPolicy: 'cache-and-network',
    context: { additionalTypenames: ['Document'] },
  }).subscribe((e) => {
    if (!e.data) return
    const data = e.data[schema] ? e.data[schema].nodes : []
    callback(
      data.map((rr) => ({
        ...rr,
        title: parse_localized(rr.title),
        timestamp: parseISO(rr.timestamp),
        validity_start: parseISO(rr.validity_start),
        validity_end: parseISO(rr.validity_end),
        categories: rr.categories.map((cc) => parse_localized(cc.title)),
      }))
    )
  })
}

export function getDocument(schema, uuid, callback) {
  schema = `get${schema}Entry`
  return query({
    query: `query($uuid: ID!) {${schema}(uuid: $uuid){title, content, timestamp, category_type, categories{uuid, title}, scopes{uuid, name}, validity_start, validity_end, validity_user, attachedFiles{nodes{uuid, name, size, mimetype, uri}}, newsworthy}}`,
    variables: { uuid: uuid },
    requestPolicy: 'cache-and-network',
    context: { additionalTypenames: ['FileAttachment', 'Category', 'Scope'] },
  }).subscribe((e) => {
    if (e && e.data && e.data[schema]) {
      const doc2 = e.data[schema]
      callback({
        ...doc2,
        categories: doc2.categories.map((cc) => ({
          ...cc,
          title: parse_localized(cc.title),
        })),
        title: parse_localized(doc2.title),
        content: parse_localized(doc2.content),
        validity_start: parseISO(doc2.validity_start),
        validity_end: parseISO(doc2.validity_end),
        validity_user: doc2.validity_user,
        timestamp: parseISO(doc2.timestamp),
        files: doc2.attachedFiles.nodes.map((ii) => ({
          uuid: ii.uuid,
          name: ii.name,
          size: ii.size,
          mimetype: ii.mimetype,
          uri: ii.uri,
          status: 'success',
        })),
      })
    }
  })
}

export async function putDocument(schema, uuid, data, target) {
  // Ignore submit while files are loading...
  if (
    data.files.filter((ii) => ii.status === 'pending' || ii.error).length != 0
  )
    return

  let params = {
    title: JSON.stringify(data.title || {}),
    content: JSON.stringify(data.content || {}),
    categories: data.categories ? data.categories.map((i) => i.uuid) : [],
    scopes: data.scopes ? data.scopes.map((i) => i.uuid) : [],
    validity_start: data.validity_start || null,
    validity_end: data.validity_end || null,
    validity_user: data.validity_user,
    addedFiles: data.files
      .filter((ii) => ii.status === 'success' && ii.content !== undefined)
      .map((ii) => ({
        uuid: ii.uuid,
        name: ii.name,
        size: ii.size,
        mimetype: ii.mimetype,
        content: ii.content,
      })),
    deletedFiles: data.files
      .filter((ii) => ii.status === 'deleted')
      .map((ii) => ii.uuid),
    newsworthy: data.newsworthy,
  }

  let args

  if (uuid)
    args = {
      query: `mutation($uuid: ID!, $data:DocumentInput!){update${schema}Entry(uuid: $uuid, data:$data){uuid}}`,
      variables: {
        data: params,
        uuid: uuid,
      },
    }
  else
    args = {
      query: `mutation($data:DocumentInput!){create${schema}Entry(data:$data){uuid}}`,
      variables: {
        data: params,
      },
    }

  await mutate(args).then((res) => {
    if (res.error || (res.error = res.errors?.[0]))
      throw parseFileUploadError(res.error.message)
    else router.goto(target)
  })

  // FIXME: pending oder so?
  // FIXME: exceptions
}

export function getCategories(schema, callback) {
  schema = `list${schema}Category`
  return query({
    query: `query{${schema}{nodes{uuid, title}}}`,
    requestPolicy: 'cache-and-network',
    context: { additionalTypenames: ['Category'] },
  }).subscribe((e) => {
    if (!e.data) return

    const data = e.data[schema] ? e.data[schema].nodes : []
    callback(
      data.map((cc) => ({ uuid: cc.uuid, title: parse_localized(cc.title) }))
    )
  })
}

export function getCategory(schema, uuid, callback) {
  schema = `get${schema}Category`
  return query({
    query: `query($uuid: ID!){${schema}(uuid: $uuid){uuid, title}}`,
    variables: { uuid: uuid },
    requestPolicy: 'cache-and-network',
  }).subscribe((e) => {
    if (e.data && e.data[schema]) {
      const data = e.data[schema]
      callback({ id: data.uuid, title: parse_localized(data.title) })
    }
  })
}

export async function putCategory(schema, uuid, data, target) {
  let args

  let params = {
    title: JSON.stringify(data.title || {}),
  }

  if (uuid)
    args = {
      query: `mutation($uuid: ID!, $data:CategoryInput!){update${schema}Category(uuid: $uuid, data:$data){uuid}}`,
      variables: {
        data: params,
        uuid: uuid,
      },
    }
  else
    args = {
      query: `mutation($data:CategoryInput!){create${schema}Category(data:$data){uuid}}`,
      variables: {
        data: params,
      },
    }

  await mutate(args).then(router.goto(target))

  // FIXME: pending oder so?
  // FIXME: exceptions
}

// pdfmake
import pdfMake from 'pdfmake/build/pdfmake'

pdfMake.fonts = {
  Roboto: {
    normal: `${window.location.origin}/files/roboto-latin-400-normal.woff`,
    bold: `${window.location.origin}/files/roboto-latin-700-normal.woff`,
    italics: `${window.location.origin}/files/roboto-latin-400-italic.woff`,
    bolditalics: `${window.location.origin}/files/roboto-latin-700-italic.woff`,
  },
  Monospace: {
    normal: `${window.location.origin}/files/roboto-mono-v13-latin-regular.woff`,
    bold: `${window.location.origin}/files/roboto-mono-v13-latin-700.woff`,
    italics: `${window.location.origin}/files/roboto-mono-v13-latin-italic.woff`,
    bolditalics: `${window.location.origin}/files/roboto-mono-v13-latin-700italic.woff`,
  },
}

import { jsLocalize } from '/@/multilang/utils.js'
import htmlToPdfmake from 'html-to-pdfmake'
import TurndownService from 'turndown'

const tss = new TurndownService()
export const makeMarkdown = (txt) => tss.turndown(txt)
export const simplifyHtml = (txt) =>
  makeHtml(makeMarkdown(txt))
    .replaceAll('strong>', 'b>')
    .replaceAll('&nbsp;', ' ')

function renderForm(schema, data, lang) {
  const noBorders = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: (i) => (i && 2) || 0,
    paddingRight: (i, node) => (i < node.table.widths.length - 1 ? 2 : 0),
    // paddingTop: (i) => (i && 2) || 0,
    // paddingBottom: (i, node) => i < node.table.body.length - 1 ? 2 : 0,
  }

  function renderItems(items) {
    if (items.length == 0) return [[{ text: '' }]]
    return items.map((ii) => [renderItem(ii)])
  }

  function renderItem(item) {
    let result = []

    if (
      item.type == 'text' ||
      item.type == 'textarea' ||
      item.type == 'select'
    ) {
      let body = []

      let text =
        item.type == 'select'
          ? jsLocalize(
            item.choices?.find((cc) => cc.value === data[item.key])?.label,
            lang
          )
          : data[item.key] || ''
      if (item.type == 'textarea')
        while (text.split('\n').length < item.rows) text += '\n'

      body.push([{ text: jsLocalize(item.label, lang), fontSize: 8 }])
      body.push([{ text }])

      result.push([
        {
          table: {
            headerRows: 0,
            widths: ['100%'],
            body: [
              [
                {
                  layout: noBorders,
                  table: {
                    headerRows: 0,
                    body,
                  },
                },
              ],
            ],
          },
        },
      ])

      const description = jsLocalize(item.description, lang)
      if (description) result.push({ text: description, fontSize: 8 })
    } else if (item.type == 'checkbox') {
      const value = data[item.key]
      const label = jsLocalize(item.label, lang)
      result.push({
        layout: noBorders,
        table: {
          headerRows: 0,
          widths: [1, 'auto'],
          body: [
            [
              {
                // margin: [-3, 7, 0, 0],
                border: [false, false, false, false],
                image: value ? 'checked' : 'unchecked',
                width: 10,
              },
              {
                margin: [10, 0, 0, 0],
                border: [false, false, false, false],
                fontSize: 10,
                text: label,
              },
            ],
          ],
        },
      })

      const description = jsLocalize(item.description, lang)
      if (description) result.push({ text: description, fontSize: 8 })
    } else if (item.type == 'content') {
      const pdf = htmlToPdfmake(simplifyHtml(jsLocalize(item.html, lang)))
      const body = [Array.isArray(pdf) ? pdf : [pdf]]

      result.push({
        layout: noBorders,
        table: {
          headerRows: 0,
          body,
        },
      })
    } else if (item.type == 'columns') {
      const label = jsLocalize(item.label, lang)
      if (label && !item.hideLabel) result.push({ text: label, fontSize: 10 })

      const body = [item.columns.map((cc) => renderItems(cc.components))]
      const widths = item.columns.map(() => 100 / item.columns.length + '%')

      result.push({
        layout: noBorders,
        table: {
          headerRows: 0,
          widths,
          body,
        },
      })
    }

    return result
  }

  return {
    layout: noBorders,
    table: {
      headerRows: 0,
      widths: ['100%'],
      body: renderItems(schema),
    },
  }
}

export const createPdf = (data) => {
  const rev = import.meta.env.GIT_REV_SHORT

  const dd = {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: 40,
    defaultStyle: {
      fontSize: 10,
    },
    footer: () => {
      return {
        columns: [
          {
            text: `${settings.branding.name} Rev. ${rev}`,
            alignment: 'center',
            fontSize: 8,
            margin: [0, 10, 0, 0],
          },
        ],
      }
    },
    ...data,
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        alignment: 'justify',
      },
      ...data.styles,
    },
    images: {
      logo: `${window.location.origin}/files/${settings.branding.logo}`,
      checked:
        'data:image/png;base64,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',
      unchecked:
        'data:image/png;base64,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',
      ...data.images,
    },
  }

  return pdfMake.createPdf(dd)
}
// xlsx
import xlsx from 'xlsx'

export { renderForm, pdfMake, xlsx }

// formatActor
import { translate } from '/@/multilang/i18n'

ftl`
    format-role-prefix = Role {$name}
    format-actor-not-available = Not available
    `

export const formatInmate = (aa) => {
  // FIXME: inmate_id
  if (!aa) return translate('format-actor-not-available').text
  return aa.last_name
    ? aa.first_name
      ? `${aa.last_name}, ${aa.first_name} (${aa.username})`
      : `${aa.last_name} (${aa.username})`
    : aa.username
}

export const formatStaff = (aa) => {
  if (!aa) return translate('format-actor-not-available').text

  return aa.last_name
    ? aa.first_name
      ? `${aa.last_name}, ${aa.first_name}${user.is_staff() ? ` (${aa.username})` : ''
      }`
      : `${aa.last_name} ${user.is_staff() ? ` (${aa.username})` : ''}`
    : aa.username
}

export const formatRole = (aa, lang) => {
  return translate('format-role-prefix', {
    name: jsLocalize(parse_localized(aa.name), lang),
  }).text
}

export const jsFormatActor = (aa, lang) => {
  if (!aa) return translate('format-actor-not-available').text
  if (['StaffRole', 'FullStaffRole'].includes(aa.__typename))
    return formatRole(aa, lang)
  else if (['Staff', 'FullStaff'].includes(aa.__typename))
    return formatStaff(aa)
  else if (['Inmate', 'FullInmate'].includes(aa.__typename))
    return formatInmate(aa)
  else return aa.username
}

export const formatActor = derived(locale, ($locale) => (aa) =>
  jsFormatActor(aa, $locale)
)

import DOMPurify from 'dompurify'

export const sanitizeHtml = (txt) => DOMPurify.sanitize(txt)

// showdown
import showdown from 'showdown'

showdown.extension('stripper', function () {
  return [
    {
      type: 'output',
      // eslint-disable-next-line no-unused-vars
      filter: function (txt, conv, opts) {
        var finalTxt = ''
        txt = sanitizeHtml(txt)
        if (txt) {
          var div = document.createElement('div')
          div.innerHTML = txt
          for (var i = 0; i < div.children.length; ++i) {
            if (div.children[i].tagName.toLowerCase() === 'p') {
              finalTxt += div.children[i].innerHTML
            }
          }
        }
        return finalTxt
      },
    },
  ]
})

// escape, from https://github.com/theSmaw/Caja-HTML-Sanitizer/blob/master/sanitizer.js#L197
var ampRe = /&/g
var ltRe = /[<]/g
var gtRe = />/g
var quotRe = /"/g

// Escapes HTML special characters in attribute values.
export const escape = (s) => {
  if (s) {
    return ('' + s)
      .replace(ampRe, '&amp;')
      .replace(ltRe, '&lt;')
      .replace(gtRe, '&gt;')
      .replace(quotRe, '&#34;')
  } else {
    return s
  }
}

const converter = new showdown.Converter({
  strikethrough: true,
  extensions: ['stripper'],
})

export const makeHtml = (txt) => converter.makeHtml(txt)

const FADE_DURATION = 200
export const popperBackdropModifier = {
  name: 'backdrop',
  phase: 'write',
  enabled: true,
  effect: ({ state }) => {
    let el = document.createElement('div')
    el.setAttribute('id', 'popper-backdrop')
    el.setAttribute(
      'style',
      [
        'backdrop-filter:blur(1px)',
        'opacity:0',
        `transition: opacity ${FADE_DURATION}ms ease-out;`,
      ].join(';')
    )
    el.classList.add(
      'fixed',
      'top-0',
      'left-0',
      'h-full',
      'w-full',
      'bg-secondary-trans',
      'z-10'
    )
    state.elements.popper.parentElement.appendChild(el)

    state.modifiersData.elBackDrop = el

    // trigger fade-in
    setTimeout(() => (el.style.opacity = 1))

    return () => {
      // retire backdrop
      el.style.opacity = 0
      setTimeout(() => {
        el.parentElement?.removeChild(el)
      }, FADE_DURATION)
    }
  },
}

export function isSoon(someDate) {
  if (!someDate || !(someDate instanceof Date))
    return console.error(`isSoon expects a Date`, someDate)

  const result = someDate - Date.now() <= 1000 * 60 * 60 * 24 * 7

  console.assert(
    new Date() > addMilliseconds(someDate, -(1000 * 60 * 60 * 24 * 7)) ===
    result
  )
  console.assert(someDate - Date.now() <= 1000 * 60 * 60 * 24 * 7 === result)
  console.assert(new Date() > addDays(someDate, -7) === result)

  return result
}

export function parseFileUploadError(errorMessage) {
  let oError

  try {
    const m = errorMessage.match(/\[GraphQL\] (?<json>.+)/)
    oError = m ? JSON.parse(m.groups?.json) : null
  } catch (e) {
    console.error('parseFileUploadError failed', errorMessage)
    return {}
  }

  if (!oError) return {}

  const { uuid, type, param } = oError

  return {
    fileUUID: uuid,
    fluentMessage: type ? [type, { param }] : ['attachment-error-upload'],
  }
}

export function captureMessage(message, ctx) {
  // eslint-disable-next-line no-undef
  if (import.meta.env.MODE === 'production') Sentry.captureMessage(message, ctx)
}

export const debounce = (func, ...args) => {
  const helper = () => {
    // console.log("debounce", func, args)
    func(...args)
  }
  // console.log("debounceCall", func, args)
  return _debounce(helper, 200, { leading: true, trailing: false })
}

// password policy description
export const generatePasswordPolicyDescription = (policy) => {
  if (!policy) return []

  const descriptions = []

  // Length requirement
  if (policy.min_length > 0) {
    descriptions.push({
      label: 'password-requirement-length',
      options: {
        minLength: policy.min_length,
      },
    })
  }

  // Character set requirements
  const charsetReqs = []
  if (policy.require_letters) {
    charsetReqs.push({
      label: 'password-requirement-letters',
    })
  }
  if (policy.require_digits) {
    charsetReqs.push({
      label: 'password-requirement-digits',
    })
  }
  if (policy.require_special) {
    charsetReqs.push({
      label: 'password-requirement-special',
    })
  }

  if (charsetReqs.length > 0) {
    descriptions.push(...charsetReqs)
  } else {
    descriptions.push({
      label: 'password-requirement-no-charset',
    })
  }

  // History requirement
  if (policy.history_check) {
    descriptions.push({
      label: 'password-requirement-history-enabled',
    })
  }

  return descriptions
}
